import { memo, MouseEvent } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconSms } from '@components/icons/phone-sms';
import { IconTrigger } from '@components/icons/trigger';
import { Stack } from '@components/stack';
import { StatusIndicator } from '@components/status-indicator';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import { ACTION_TYPES, getActionLabel } from 'ui/actions/action-config';
import { useJourneyContext } from 'ui/journey-builder/base-builder';
import { Assignees, NodeActions } from 'ui/journey-builder/node-types/common';
import { JourneyNodeActionCard, NodeContainer } from 'ui/journey-builder/node-types/node-container';
import { JourneyNode } from 'ui/journey-builder/node-types/types';
import { isInTemplateRoute } from 'ui/router';

import { useEditJourneyActionFormOverlay } from './edit-journey-action-form-overlay';
import { useOverlayPreview } from './overlay-preview';

const ACTION_TYPE = ACTION_TYPES.combo_send_form;

export const Node = memo((node: JourneyNode<typeof ACTION_TYPE>) => {
  const { t } = useScopedTranslate(nodeLocale);
  const { journeyActionForms } = useJourneyContext();
  const showEditJourneyActionFormOverlay = useEditJourneyActionFormOverlay();
  const showOverlayPreview = useOverlayPreview();
  const isPreviewOnly = isInTemplateRoute();
  const journeyActionForm = journeyActionForms[node.id];

  const formName = journeyActionForm?.name ?? t('untitledForm');
  const formId = journeyActionForm?.shortId;
  const hasEmailEnabled = node?.data?.values?.emailNotify;
  const hasSmsEnabled = node?.data?.values?.smsNotify;

  const handleEditClick = () => {
    if (formId) {
      showEditJourneyActionFormOverlay({ formId });
    }
  };

  const handlePreviewClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (formId) {
      showOverlayPreview({
        formId,
      });
    }
  };

  return (
    <NodeContainer node={node}>
      <JourneyNodeActionCard
        node={node}
        label={getActionLabel(ACTION_TYPE)}
        renderBelowDescription={
          <div style={{ paddingTop: pxToRem(8), paddingRight: pxToRem(8) }}>
            <Card spaceY={2} spaceX={4}>
              <Stack align="center" orientation="horizontal" justify="space-between">
                <Stack align="center" space={3} orientation="horizontal" style={{ minWidth: 0 }}>
                  <IconForm />
                  <Tooltip content={formName}>
                    <NameContainer>
                      <Text font="semibold14" text={formName ?? t('untitledForm')} />
                    </NameContainer>
                  </Tooltip>
                </Stack>
                <ButtonWrapper>
                  {isPreviewOnly ? (
                    <Button
                      className="template-node-preview-button"
                      variant="ghost"
                      dataTestid="preview-form"
                      onClick={handlePreviewClick}
                      text={t('core.preview')}
                    />
                  ) : (
                    <Button
                      variant="ghost"
                      dataTestid="edit-form"
                      onClick={handleEditClick}
                      text={t('core.edit')}
                    />
                  )}
                </ButtonWrapper>
              </Stack>
            </Card>
          </div>
        }
      >
        <NodeActions
          node={node}
          customExecutionIndicators={
            <Stack orientation="horizontal">
              {hasEmailEnabled && (
                <div style={{ marginRight: pxToRem(-8), position: 'relative', zIndex: 1 }}>
                  <StatusIndicator variant="notStarted" icon={<IconEmail />} />
                </div>
              )}
              {hasSmsEnabled && (
                <div style={{ marginRight: pxToRem(-8), position: 'relative', zIndex: 2 }}>
                  <StatusIndicator variant="notStarted" icon={<IconSms />} />
                </div>
              )}
              {node.data?.isAutomatic ? (
                <div style={{ position: 'relative', zIndex: 3 }}>
                  <StatusIndicator variant="notStarted" icon={<IconTrigger />} />
                </div>
              ) : (
                <Assignees assignees={node.data?.assignees} />
              )}
            </Stack>
          }
        />
      </JourneyNodeActionCard>
    </NodeContainer>
  );
});

const nodeLocale = {
  en: {
    untitledForm: 'Untitled form',
  },
};

const NameContainer = styled.div({
  flexGrow: 1,
  minWidth: 0,
  ...lineClamp('-webkit-box', 2, true),
  wordBreak: 'normal',
});

const ButtonWrapper = styled.div({
  flexShrink: 0,
  whiteSpace: 'nowrap',
});
